import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptor de respuesta: manejo unificado de 401 y errores de red
const PUBLIC_PATHS = ["/", "/tienda", "/carrito"];
const isPublicPath = (path) => {
  if (PUBLIC_PATHS.includes(path)) return true;
  if (path && path.startsWith("/producto/")) return true;
  return false;
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // 401 -> sesión expirada: limpiar token y redirigir a login (excepto en rutas públicas)
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== "/login" && !isPublicPath(path)) {
        try { localStorage.removeItem("token"); } catch {}
        try { sessionStorage.setItem("session_expired", "1"); } catch {}
        window.location.href = "/login";
      } else if (isPublicPath(path)) {
        // Solo limpiar token sin redirigir para no romper la landing
        try { localStorage.removeItem("token"); } catch {}
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  // Sesión expirada
  if (err?.response?.status === 401) return "Sesión expirada. Volvé a iniciar sesión.";
  // Sin respuesta del servidor (network, CORS, timeout, backend reiniciando)
  if (!err?.response) {
    return "Sin conexión con el servidor. Revisá tu internet o refrescá la página.";
  }
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Error desconocido";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export function formatMoney(n) {
  const v = Number(n || 0);
  return `Q ${v.toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Fechas: siempre en zona horaria de Guatemala (America/Guatemala, UTC-6).
// Cuando el backend envía "YYYY-MM-DD" lo respetamos como fecha local (sin re-shift).
// Cuando envía un ISO con hora, lo convertimos a la fecha real en Guatemala.
const GT_TZ = "America/Guatemala";

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    // Si es sólo YYYY-MM-DD, parseamos como fecha local de Guatemala (no como UTC)
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split("-").map(Number);
      const dt = new Date(y, m - 1, d);  // fecha local sin tz shift
      return dt.toLocaleDateString("es-GT", { day: "2-digit", month: "short", year: "numeric" });
    }
    return new Date(iso).toLocaleDateString("es-GT", {
      day: "2-digit", month: "short", year: "numeric", timeZone: GT_TZ,
    });
  } catch {
    return iso;
  }
}

// Fecha "hoy" en zona horaria de Guatemala (YYYY-MM-DD).
export function todayLocalIso() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: GT_TZ, year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(now);
  const y = parts.find(p => p.type === "year").value;
  const m = parts.find(p => p.type === "month").value;
  const d = parts.find(p => p.type === "day").value;
  return `${y}-${m}-${d}`;
}

// Convierte cualquier ISO/date a "YYYY-MM-DD" en Guatemala (para filtros).
export function toGtDate(iso) {
  if (!iso) return "";
  if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: GT_TZ, year: "numeric", month: "2-digit", day: "2-digit"
    }).formatToParts(new Date(iso));
    const y = parts.find(p => p.type === "year").value;
    const m = parts.find(p => p.type === "month").value;
    const d = parts.find(p => p.type === "day").value;
    return `${y}-${m}-${d}`;
  } catch {
    return "";
  }
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-GT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: GT_TZ,
    });
  } catch {
    return iso;
  }
}

// Construye un resumen tipo frase del producto de un pedido.
// Ejemplo: "CABECERA KING CAPITONADA COLOR ROSA EN TERCIOPELO — TAMAÑO KING"
export function productSummary(item) {
  if (!item) return "";
  const parts = [(item.product_name || "").toUpperCase().trim()];
  if (item.color) parts.push(`COLOR ${item.color.toUpperCase()}`);
  if (item.tela) parts.push(`EN ${item.tela.toUpperCase()}`);
  if (item.tamano) parts.push(`TAMAÑO ${item.tamano.toUpperCase()}`);
  if (item.estilo) parts.push(`ESTILO ${item.estilo.toUpperCase()}`);
  return parts.filter(Boolean).join(" ");
}

export default api;
